import React from 'react'
import { observable, makeObservable, computed } from 'mobx'
import { observer } from 'mobx-react'
import {
  FjText,
  ContainerDiv,
  Loader,
  FeedEmptyContent,
  FilterDropdownButton,
  FeedEmptyContentProps,
} from 'src/components/Common'
import { Colors } from 'src/constants/colors'
import { sharedAppStateStore } from 'src/store/AppStateStore'
import { APIProvider } from 'src/network/APIProvider'
import LibraryCardHorizontal from './LibraryCardHorizontal'
import { QueueElement } from 'src/models/QueueItem'
import { ConsumptionChartContentType } from 'src/components/Dashboard/ConsumptionChart'
import { ProgressUser } from 'src/components/Learn/ContentProgressDetails'
import { sharedDataStore } from 'src/store/DataStore'
import { LibraryCardRow } from 'src/components/Library/LibraryCardRow'
import { getContentCategoryTitle } from 'src/utils/content'
import { LibraryCategory } from 'src/pages/Library'
import FjPopover, { FjPopoverContent } from 'src/components/Common/FjPopover'

type UserProgressContentTypeOptionType = { [key in ConsumptionChartContentType]?: string }

const UserProgressTitle: React.FC<{
  user: ProgressUser
  contentType: LibraryCategory
  display?: 'horizontal' | 'vertical'
}> = ({ user, contentType, display }) => {
  if (display === 'horizontal') {
    return (
      <FjText display="block" fontWeight="semi-bold" textAlign="left" color={Colors.midnightBlue} fontSize="24px">
        My {getContentCategoryTitle(contentType)}
      </FjText>
    )
  }
  return (
    <FjText fontWeight="bold" fontSize="25px" color={Colors.midnightBlue} marginBottom textAlign="left" display="block">
      {sharedDataStore.user.id === user.id ? 'My Progress' : `${user.fullName}'s Progress`}
    </FjText>
  )
}

interface IUserProgressProps {
  user: ProgressUser
  contentTypeChartOptions?: ConsumptionChartContentType[]
  display?: 'horizontal' | 'vertical'
  emptyContentProps?: FeedEmptyContentProps
  height?: string
}

@observer
export class UserProgress extends React.Component<IUserProgressProps> {
  @observable loading = false
  @observable content: QueueElement[] = []
  @observable contentType: ConsumptionChartContentType = 'course'
  @observable contentTypeChartOptions: UserProgressContentTypeOptionType

  @computed get contentTypeOptions() {
    return Object.keys(this.contentTypeChartOptions).map((key: ConsumptionChartContentType) => ({
      id: key,
      key: key,
      value: key,
      label: this.contentTypeChartOptions[key],
    }))
  }

  @computed get contentTypeValue() {
    return [
      {
        key: this.contentType,
        value: this.contentType,
        id: this.contentType,
        label: this.contentType,
      },
    ]
  }

  constructor(props: IUserProgressProps) {
    super(props)
    makeObservable(this)

    const defaultContentTypeChartOptions: UserProgressContentTypeOptionType = {
      course: 'Courses',
      playlist: 'Collections',
      prompt: 'Prompts',
      learningpath: 'Learning Paths',
      asset: 'Assets',
    }

    if (props.contentTypeChartOptions) {
      this.contentTypeChartOptions = props.contentTypeChartOptions.reduce((options, contentTypeOption) => {
        options[contentTypeOption] = defaultContentTypeChartOptions[contentTypeOption]
        return options
      }, {}) as UserProgressContentTypeOptionType
    } else {
      this.contentTypeChartOptions = { ...defaultContentTypeChartOptions }
    }
  }

  componentDidMount(): void {
    this.fetchData()
  }

  componentDidUpdate(prevProps: Readonly<IUserProgressProps>, prevState: Readonly<{}>, snapshot?: any): void {
    if (prevProps.user.id !== this.props.user.id) this.fetchData()
  }

  fetchData = async () => {
    try {
      this.loading = true
      const data = await APIProvider.fetchUserConsumptionData(this.props.user.id, this.contentType)
      this.content = data.sort((a, b) => a.progress - b.progress)
    } catch (err) {
      sharedAppStateStore.handleError(err)
    } finally {
      this.loading = false
    }
  }

  setContentType = (contentType: ConsumptionChartContentType) => {
    this.contentType = contentType
    this.fetchData()
  }

  render() {
    const { user, display } = this.props

    return (
      <>
        <ContainerDiv marginBottom display="flex" flexWrap="wrap" alignItems="center" justifyContent="space-between">
          <UserProgressTitle user={user} contentType={this.contentType as LibraryCategory} display={display} />
          <FjPopover
            content={
              <FjPopoverContent
                options={this.contentTypeOptions}
                values={this.contentTypeValue}
                addFilterOption={(option) => this.setContentType(option.key as ConsumptionChartContentType)}
              />
            }
          >
            <FilterDropdownButton selected title={`Content Type | ${this.contentTypeChartOptions[this.contentType]}`} />
          </FjPopover>
        </ContainerDiv>

        {this.loading ? (
          <Loader />
        ) : this.content && this.content.length ? (
          <>
            {display === 'horizontal' ? (
              <>
                <LibraryCardRow objs={this.content as any} />
              </>
            ) : (
              <ContainerDiv textAlign="left" height={this.props.height} overflow="auto">
                {this.content.map((item, i) => (
                  <LibraryCardHorizontal
                    key={`consumption-card-${i}`}
                    showDivider
                    obj={item}
                    bigProgress
                    progressUser={user}
                  />
                ))}
              </ContainerDiv>
            )}
          </>
        ) : (
          <ContainerDiv textAlign="left" height={this.props.height} overflow="auto">
            {this.props.emptyContentProps ? (
              <FeedEmptyContent {...this.props.emptyContentProps} />
            ) : (
              <FeedEmptyContent />
            )}
          </ContainerDiv>
        )}
      </>
    )
  }
}
